import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Box, Button, Container, Typography, Paper, Alert, Stack } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { logger, getErrorDetails } from '../utils/logger';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  copied: boolean;
}

export default class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      copied: false,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Log error to backend
    logger.error('React Error Boundary caught error', {
      componentStack: errorInfo.componentStack,
      errorBoundary: true,
    }, error);

    this.setState({
      errorInfo,
    });
  }

  handleReset = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      copied: false,
    });
  };

  handleCopyError = (): void => {
    const { error, errorInfo } = this.state;
    if (!error) return;

    const details = getErrorDetails(
      error.message,
      {
        componentStack: errorInfo?.componentStack,
      },
      error
    );

    navigator.clipboard.writeText(details).then(() => {
      this.setState({ copied: true });
      setTimeout(() => this.setState({ copied: false }), 3000);
    });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <Container maxWidth="md" sx={{ py: 8 }}>
          <Paper
            elevation={0}
            sx={{
              p: 4,
              borderRadius: 3,
              border: '1px solid #E2E8F0',
              textAlign: 'center',
            }}
          >
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                backgroundColor: '#FEE2E2',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 24px',
              }}
            >
              <ErrorOutlineIcon sx={{ fontSize: 48, color: '#EF4444' }} />
            </Box>

            <Typography variant="h4" fontWeight={700} gutterBottom sx={{ mb: 2 }}>
              Something Went Wrong
            </Typography>

            <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
              We're sorry, but an unexpected error occurred. The error has been logged and our team has been notified.
            </Typography>

            <Stack direction="row" spacing={2} justifyContent="center" sx={{ mb: 4 }}>
              <Button
                variant="contained"
                startIcon={<RefreshIcon />}
                onClick={this.handleReset}
                sx={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  px: 3,
                  '&:hover': {
                    background: 'linear-gradient(135deg, #5568d3 0%, #5a3a7a 100%)',
                  },
                }}
              >
                Try Again
              </Button>

              <Button
                variant="outlined"
                startIcon={<ContentCopyIcon />}
                onClick={this.handleCopyError}
                sx={{ px: 3 }}
              >
                {this.state.copied ? 'Copied!' : 'Copy Error Details'}
              </Button>
            </Stack>

            {import.meta.env.DEV && this.state.error && (
              <Alert severity="error" sx={{ textAlign: 'left', mt: 3 }}>
                <Typography variant="body2" fontWeight={600} gutterBottom>
                  Error: {this.state.error.message}
                </Typography>
                {this.state.error.stack && (
                  <Typography
                    variant="caption"
                    component="pre"
                    sx={{
                      mt: 1,
                      p: 1,
                      backgroundColor: '#1E293B',
                      color: '#E2E8F0',
                      borderRadius: 1,
                      overflow: 'auto',
                      maxHeight: 200,
                    }}
                  >
                    {this.state.error.stack}
                  </Typography>
                )}
              </Alert>
            )}
          </Paper>
        </Container>
      );
    }

    return this.props.children;
  }
}
